/**
 * The submitted screen's illustrations, all drawn.
 *
 * Kept apart from the screen itself because they are artwork with no state:
 * paths, gradients and fixed coordinates. The screen reads as a page of copy
 * and numbers once these are out of it.
 */
import { type ReactElement } from 'react';

/** The tick that lands first: a filled disc with a soft ring, so it reads as a
 * stamp rather than a button. */
export function SuccessMark() {
  return (
    <svg viewBox="0 0 140 140" aria-hidden className="h-[132px] w-[132px]">
      <defs>
        <linearGradient id="ok-face" x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0%" stopColor="#22c55e" />
          <stop offset="100%" stopColor="#15803d" />
        </linearGradient>
      </defs>
      <circle cx="70" cy="70" r="66" fill="#22c55e" opacity={0.12} />
      <circle cx="70" cy="70" r="56" fill="url(#ok-face)" />
      <path
        d="M44 71 62 89 97 51"
        fill="none"
        stroke="white"
        strokeWidth="11"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * left%, top%, rotation, kind, colour.
 *
 * Fixed, not random: artwork that reshuffles on every render reads as noise.
 * And kept to the margins — the headline and the line under it own the middle
 * of this card, and paper thrown across a sentence is just something to read
 * around. Nothing is placed between 14% and 70% across above the stats card.
 */
const CONFETTI: Array<
  [number, number, number, 'bar' | 'curl' | 'dot' | 'tri', string]
> = [
  // Down the left, beside and under the tick.
  [1, 30, -25, 'bar', '#fbbf24'],
  [4, 8, 40, 'curl', '#22c55e'],
  [2, 62, 0, 'dot', '#3b82f6'],
  [6, 46, 15, 'tri', '#f97316'],
  [9, 18, -40, 'bar', '#a855f7'],
  [5, 78, 30, 'curl', '#ef4444'],
  [11, 68, -10, 'bar', '#22c55e'],
  [8, 88, 55, 'dot', '#fbbf24'],
  [12, 38, -30, 'tri', '#3b82f6'],

  // A thin scatter across the very top, above the headline.
  [22, 2, 20, 'curl', '#22c55e'],
  [31, 5, -50, 'bar', '#f97316'],
  [40, 1, 25, 'dot', '#a855f7'],
  [49, 6, -15, 'bar', '#fbbf24'],
  [58, 2, 35, 'tri', '#ef4444'],
  [66, 5, -35, 'curl', '#3b82f6'],

  // Down the right, around the 100% plate.
  [74, 22, 10, 'bar', '#22c55e'],
  [78, 58, 45, 'dot', '#f97316'],
  [82, 12, -20, 'bar', '#a855f7'],
  [86, 74, 30, 'tri', '#22c55e'],
  [89, 34, -45, 'curl', '#fbbf24'],
  [92, 66, 20, 'bar', '#3b82f6'],
  [95, 16, -15, 'dot', '#ef4444'],
  [97, 48, 35, 'bar', '#22c55e'],
  [99, 82, -25, 'tri', '#a855f7'],
];

function ConfettiPiece({
  kind,
  colour,
}: {
  kind: 'bar' | 'curl' | 'dot' | 'tri';
  colour: string;
}) {
  if (kind === 'curl') {
    return (
      <svg viewBox="0 0 12 8" className="h-2.5 w-4">
        <path
          d="M1 6Q6 0 11 6"
          fill="none"
          stroke={colour}
          strokeWidth="2.4"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  if (kind === 'dot') {
    return (
      <span
        className="block h-2 w-2 rounded-full"
        style={{ background: colour }}
      />
    );
  }
  if (kind === 'tri') {
    return (
      <svg viewBox="0 0 10 9" className="h-2.5 w-3">
        <path d="M5 0 10 9H0Z" fill={colour} />
      </svg>
    );
  }
  return (
    <span
      className="block h-2 w-4 rounded-full"
      style={{ background: colour }}
    />
  );
}

export function Confetti() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      {CONFETTI.map(([left, top, rotate, kind, colour]) => (
        <span
          key={`${left}-${top}`}
          className="absolute"
          style={{
            left: `${left}%`,
            top: `${top}%`,
            transform: `rotate(${rotate}deg)`,
          }}
        >
          <ConfettiPiece kind={kind} colour={colour} />
        </span>
      ))}
    </div>
  );
}

/** The leaf sweeping out of the bottom-right corner: two arcs of the brand
 * green, fading as they go, so the card has a floor without a hard edge. */
export function CornerSwoosh() {
  return (
    <svg
      viewBox="0 0 300 220"
      aria-hidden
      className="pointer-events-none absolute -bottom-6 -right-6 h-[300px] w-[420px]"
    >
      <defs>
        <linearGradient id="swoosh-a" x1="0" y1="1" x2="0.8" y2="0">
          <stop offset="0%" stopColor="#22c55e" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#86efac" stopOpacity="0.15" />
        </linearGradient>
        <linearGradient id="swoosh-b" x1="0" y1="1" x2="0.8" y2="0">
          <stop offset="0%" stopColor="#4ade80" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#bbf7d0" stopOpacity="0.08" />
        </linearGradient>
      </defs>
      <path
        d="M20 240C20 130 120 40 300 20 150 66 70 140 66 240Z"
        fill="url(#swoosh-b)"
      />
      <path
        d="M96 240C96 150 176 74 300 56 190 96 138 160 134 240Z"
        fill="url(#swoosh-a)"
      />
    </svg>
  );
}

/** The rim ticks that make a coin read as milled rather than as a disc. */
const KNURL = Array.from({ length: 36 }, (_, i) => i * 10);

/** The stack, bottom disc first. */
const STACK = [128, 116, 104, 92];

/**
 * The coins the programme pays in.
 *
 * Built the way a coin actually reads: light from the upper left, a rim a shade
 * darker than the face, milling around the edge, and a visible side wall under
 * each disc so the pile has depth. The mark is struck into the face — cut by a
 * shadow above and a highlight below — rather than printed on it, which is the
 * difference between a coin and a sticker.
 */
export function CoinStack() {
  return (
    <svg viewBox="0 0 200 160" aria-hidden className="h-[130px] w-[162px]">
      <defs>
        <linearGradient id="coin-face" x1="0.15" y1="0" x2="0.85" y2="1">
          <stop offset="0%" stopColor="#fef3c7" />
          <stop offset="35%" stopColor="#fcd34d" />
          <stop offset="70%" stopColor="#f59e0b" />
          <stop offset="100%" stopColor="#b45309" />
        </linearGradient>
        <linearGradient id="coin-rim" x1="0.2" y1="0" x2="0.8" y2="1">
          <stop offset="0%" stopColor="#fcd34d" />
          <stop offset="55%" stopColor="#d97706" />
          <stop offset="100%" stopColor="#92400e" />
        </linearGradient>
        <linearGradient id="coin-wall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#d97706" />
          <stop offset="100%" stopColor="#7c2d12" />
        </linearGradient>
        <radialGradient id="coin-shadow">
          <stop offset="0%" stopColor="#78350f" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#78350f" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="coin-gloss" x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.75" />
          <stop offset="60%" stopColor="#fff" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* What the pile sits on. */}
      <ellipse cx="96" cy="140" rx="76" ry="16" fill="url(#coin-shadow)" />

      {/* The stack: each disc is a wall plus a face, not a lone ellipse. */}
      {STACK.map((cy) => (
        <g key={cy}>
          <path
            d={`M16 ${cy}a42 16 0 0 0 84 0v11a42 16 0 0 1-84 0Z`}
            fill="url(#coin-wall)"
          />
          <ellipse cx="58" cy={cy} rx="42" ry="16" fill="url(#coin-rim)" />
          <ellipse cx="58" cy={cy} rx="35" ry="11" fill="url(#coin-face)" />
          {/* The light catching the near edge of the face. */}
          <path
            d={`M25 ${cy - 2}a34 10 0 0 1 37 -7`}
            fill="none"
            stroke="#fffbeb"
            strokeWidth="2"
            strokeLinecap="round"
            opacity="0.7"
          />
        </g>
      ))}

      {/* The coin standing against the stack, face to the viewer. */}
      <g transform="translate(132 74)">
        <ellipse cy="52" rx="40" ry="9" fill="url(#coin-shadow)" />

        <circle r="46" fill="url(#coin-rim)" />
        {/* Milling: short ticks stepped around the rim. */}
        <g stroke="#92400e" strokeWidth="2.2" opacity="0.45">
          {KNURL.map((deg) => (
            <line
              key={deg}
              y1="-46"
              y2="-41"
              transform={`rotate(${deg})`}
              strokeLinecap="round"
            />
          ))}
        </g>
        <circle r="39" fill="url(#coin-face)" />
        <circle
          r="32"
          fill="none"
          stroke="#b45309"
          strokeWidth="2"
          opacity="0.35"
        />

        {/* Struck, not printed: a dark edge above the mark and a light face
            below it reads as metal displaced. */}
        <text
          y="15"
          textAnchor="middle"
          className="font-primary"
          fontSize="46"
          fontWeight="700"
          fill="#92400e"
          opacity="0.55"
        >
          F
        </text>
        <text
          y="13"
          textAnchor="middle"
          className="font-primary"
          fontSize="46"
          fontWeight="700"
          fill="#fffbeb"
        >
          F
        </text>

        {/* The light itself: a band inside the upper-left of the face, and a
            thin catch on the rim above it. */}
        <path
          d="M-34 -14A37 37 0 0 1 -14 -34 26 26 0 0 0 -34 -14Z"
          fill="url(#coin-gloss)"
        />
        <path
          d="M-32 -30A44 44 0 0 1 -6 -45"
          fill="none"
          stroke="#fffbeb"
          strokeWidth="3"
          strokeLinecap="round"
          opacity="0.75"
        />
      </g>

      {/* A coin lying flat in front, so the pile has a foreground. */}
      <g transform="translate(52 136)">
        <path
          d="M-34 0a34 13 0 0 0 68 0v7a34 13 0 0 1-68 0Z"
          fill="url(#coin-wall)"
        />
        <ellipse rx="34" ry="13" fill="url(#coin-rim)" />
        <ellipse rx="27" ry="9" fill="url(#coin-face)" />
      </g>

      {/* Sparks, so the pile reads as a reward and not as change. */}
      <g fill="#fcd34d">
        <path d="M24 40 27 50 37 53 27 56 24 66 21 56 11 53 21 50Z" />
        <path d="M180 108 182 114 188 116 182 118 180 124 178 118 172 116 178 114Z" />
        <path d="M162 26 163.5 30.5 168 32 163.5 33.5 162 38 160.5 33.5 156 32 160.5 30.5Z" />
      </g>
    </svg>
  );
}

/** One rung's badge: a rounded plate in the rung's own colour, with its mark
 * cut into it. Drawn rather than an icon in a tinted box, so the four read as a
 * set of awards. */
type RungKind = 'submitted' | 'approved' | 'rated' | 'rewards';

const BADGE_STROKE = {
  fill: 'none',
  stroke: 'white',
  strokeWidth: 2.2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** Each rung's two-stop gradient, light to dark. */
const BADGE_TINT: Record<RungKind, [string, string]> = {
  submitted: ['#c084fc', '#7c3aed'],
  approved: ['#60a5fa', '#2563eb'],
  rated: ['#4ade80', '#16a34a'],
  rewards: ['#fbbf24', '#d97706'],
};

/** The glyph inside the badge — what that rung is. */
const BADGE_GLYPH: Record<RungKind, ReactElement> = {
  submitted: <path d="M24 32V17m-7 7 7-7 7 7M15 34h18" {...BADGE_STROKE} />,
  approved: (
    <>
      <circle cx="24" cy="23" r="11" {...BADGE_STROKE} />
      <path d="M19 23.5 22.5 27 30 19" {...BADGE_STROKE} />
    </>
  ),
  rated: (
    <g fill="white">
      {[13, 24, 35].map((cx) => (
        <path
          key={cx}
          transform={`translate(${cx} 24) scale(0.42)`}
          d="M0 -12 3.5 -3.7 12 -3.7 5.2 1.5 7.8 10 0 5 -7.8 10 -5.2 1.5 -12 -3.7 -3.5 -3.7Z"
        />
      ))}
    </g>
  ),
  rewards: (
    <>
      <path d="M13 22h22v13H13Zm-1-7h24v7H12Zm12 0v20" {...BADGE_STROKE} />
      <path
        d="M24 15c-3-6-11-4-8 0 2 2 8 0 8 0Zm0 0c3-6 11-4 8 0-2 2-8 0-8 0Z"
        {...BADGE_STROKE}
      />
    </>
  ),
};

/** A shield for the one that is earned rather than done; a rounded square for
 * the rest. */
function BadgePlate({ kind, fill }: { kind: RungKind; fill: string }) {
  if (kind === 'rated') {
    return (
      <path
        d="M24 3 43 10v16c0 11-8 17-19 21C13 43 5 37 5 26V10Z"
        fill={fill}
      />
    );
  }
  return <rect x="4" y="4" width="40" height="40" rx="13" fill={fill} />;
}

export function LadderBadge({ kind }: { kind: RungKind }) {
  const [from, to] = BADGE_TINT[kind];
  const id = `badge-${kind}`;
  return (
    <svg viewBox="0 0 48 48" aria-hidden className="h-12 w-12">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0%" stopColor={from} />
          <stop offset="100%" stopColor={to} />
        </linearGradient>
      </defs>
      <BadgePlate kind={kind} fill={`url(#${id})`} />
      {BADGE_GLYPH[kind]}
    </svg>
  );
}
